import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Button,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { processApi } from '../api/services';
import { useAuthStore } from '../store/authStore';
import { useCryptoKeyStore } from '../features/encryption';

export function ProcessListPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { activeKey, gkdkSlots, loadGKDKSlots } = useCryptoKeyStore();
  const [gkdkLoadAttempted, setGkdkLoadAttempted] = useState(false);
  const [showEnded, setShowEnded] = useState(false);
  const isCitizen = user?.roles.includes('CITIZEN');

  // Load GKDK slots on mount to get authority IDs
  useEffect(() => {
    if (activeKey && gkdkSlots.length === 0 && !gkdkLoadAttempted) {
      setGkdkLoadAttempted(true);
      loadGKDKSlots();
    }
  }, [activeKey, gkdkSlots.length, loadGKDKSlots, gkdkLoadAttempted]);

  // Get the first authority ID from GKDK slots (employee's authority)
  const authorityId = gkdkSlots.length > 0 ? gkdkSlots[0].authorityId : undefined;

  const { data, isLoading, error } = useQuery({
    queryKey: ['processes', showEnded, authorityId],
    queryFn: () => processApi.getProcesses(showEnded, authorityId),
  });

  const { data: definitions } = useQuery({
    queryKey: ['process-definitions'],
    queryFn: processApi.getDefinitions,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Fehler beim Laden der Prozesse</Alert>;
  }

  const processes = data?.processes || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Prozesse</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={showEnded}
                onChange={(e) => setShowEnded(e.target.checked)}
              />
            }
            label="Abgeschlossene anzeigen"
          />
          {isCitizen && definitions && definitions.length > 0 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/processes/new')}
            >
              Neuer Antrag
            </Button>
          )}
        </Box>
      </Box>

      {processes.length === 0 ? (
        <Card>
          <CardContent>
            <Alert severity="info">
              Keine Prozesse vorhanden
            </Alert>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Prozess</TableCell>
                <TableCell>Referenz</TableCell>
                <TableCell>Gestartet am</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {processes.map((process) => (
                <TableRow
                  key={process.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/processes/${process.id}`)}
                >
                  <TableCell>
                    <Typography variant="subtitle2">
                      {process.processDefinitionName || process.processDefinitionKey}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {process.businessKey || process.id.substring(0, 8)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {process.startTime
                      ? new Date(process.startTime).toLocaleDateString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {process.ended ? (
                      <Chip label="Abgeschlossen" size="small" color="default" />
                    ) : (
                      <Chip label="Aktiv" size="small" color="success" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Details anzeigen">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/processes/${process.id}`);
                        }}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
